import React, { useState, useEffect } from 'react';
import { 
  Zap, Plus, Edit, Trash2, Eye, Save, Loader2, X, 
  ArrowRight, ShoppingCart, Gift, Mail, TrendingUp,
  BarChart3, Copy, ExternalLink, Play, Pause, DollarSign, Percent
} from 'lucide-react';
import { 
  getAllFunnels, createFunnel, updateFunnel, deleteFunnel,
  getFunnelAnalytics
} from '../firebase/funnels';
import { getAllProducts } from '../firebase/products';
import { EmptyState } from './EmptyStates';
import { CardSkeleton } from './LoadingStates';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';
import { FunnelAnalyticsModal } from './FunnelAnalyticsModal';

// Funnel Step Types
const STEP_TYPES = {
  optIn: { label: 'Opt-In', icon: Mail, color: 'blue' },
  tripwire: { label: 'Tripwire', icon: Gift, color: 'yellow' },
  mainOffer: { label: 'Main Offer', icon: ShoppingCart, color: 'green' },
  upsell: { label: 'Upsell', icon: TrendingUp, color: 'purple' },
  orderBump: { label: 'Order Bump', icon: Zap, color: 'orange' }
};

// Funnel Step Component
function FunnelStep({ step, stepNumber, onEdit, onDelete, onMove, products = [] }) {
  const stepConfig = STEP_TYPES[step.type] || STEP_TYPES.optIn;
  const Icon = stepConfig.icon;
  
  // Look up product title dynamically instead of using stored productTitle
  const product = step.productId ? products.find(p => p.id === step.productId) : null;
  const productTitle = product?.title || step.productId || '';
  
  return (
    <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 p-6 hover:border-[#c5a059] transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white`}
            style={{ backgroundColor: stepConfig.color === 'blue' ? '#3b82f6' : 
                              stepConfig.color === 'yellow' ? '#eab308' :
                              stepConfig.color === 'green' ? '#10b981' :
                              stepConfig.color === 'purple' ? '#8b5cf6' : '#f97316' }}>
            <Icon size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{stepConfig.label}</h3>
            <p className="text-sm text-gray-500">Step {stepNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(step)}
            className="p-2 text-gray-400 hover:text-[#c5a059] transition"
            title="Edit step"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => onDelete(step.id)}
            className="p-2 text-gray-400 hover:text-red-600 transition"
            title="Delete step"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <div className="space-y-2">
        {step.productId && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Product:</span> {productTitle}
          </div>
        )}
        {step.title && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Title:</span> {step.title}
          </div>
        )}
        {step.orderBump && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Order Bump:</span> {step.orderBump.title}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminFunnelsTab({ showToast }) {
  const [funnels, setFunnels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFunnel, setEditingFunnel] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [analytics, setAnalytics] = useState({});
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsFunnel, setAnalyticsFunnel] = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [funnelsData, productsData] = await Promise.all([
        getAllFunnels(),
        getAllProducts()
      ]);
      setFunnels(funnelsData);
      setProducts(productsData);
      
      // Load analytics for each funnel
      const analyticsPromises = funnelsData.map(async (funnel) => {
        const analytics = await getFunnelAnalytics(funnel.id);
        return { funnelId: funnel.id, analytics };
      });
      const analyticsResults = await Promise.all(analyticsPromises);
      const analyticsMap = {};
      analyticsResults.forEach(({ funnelId, analytics }) => {
        analyticsMap[funnelId] = analytics;
      });
      setAnalytics(analyticsMap);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load funnels', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (funnelData) => {
    try {
      let result;
      if (editingFunnel) {
        result = await updateFunnel(editingFunnel.id, funnelData);
      } else {
        result = await createFunnel(funnelData);
      }

      if (result.success) {
        showToast(`Funnel ${editingFunnel ? 'updated' : 'created'} successfully`, 'success');
        setShowModal(false);
        setEditingFunnel(null);
        loadData();
      } else {
        showToast('Error saving funnel: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving funnel: ' + error.message, 'error');
    }
  };

  const handleDelete = async (funnelId) => {
    if (!confirm('Delete this funnel? This action cannot be undone.')) return;
    
    try {
      const result = await deleteFunnel(funnelId);
      if (result.success) {
        showToast('Funnel deleted successfully', 'success');
        loadData();
      } else {
        showToast('Error deleting funnel: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error deleting funnel: ' + error.message, 'error');
    }
  };

  const handleToggleStatus = async (funnelId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'draft' : 'active';
      const result = await updateFunnel(funnelId, { status: newStatus });
      if (result.success) {
        showToast(`Funnel ${newStatus}`, 'success');
        loadData();
      }
    } catch (error) {
      showToast('Error updating funnel', 'error');
    }
  };

  const copyFunnelUrl = (slug) => {
    const url = `${window.location.origin}/funnel/${slug}`;
    navigator.clipboard.writeText(url);
    showToast('Funnel URL copied to clipboard', 'success');
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <CardSkeleton count={6} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-gray-800">Funnel Builder</h2>
            <HelpTooltip
              title="Sales Funnel Builder"
              content="Create high-converting sales funnels that guide customers from awareness to purchase. Build opt-in pages, tripwire offers, main products, and upsells. Each funnel has a unique URL you can share. Track conversions and optimize your funnel performance."
            />
          </div>
          <p className="text-gray-600 mt-1">Create high-converting sales funnels with opt-ins, tripwires, and upsells</p>
        </div>
        <button
          onClick={() => {
            setEditingFunnel(null);
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition-all hover:shadow-lg"
        >
          <Plus size={18} className="mr-2" />
          New Funnel
        </button>
      </div>

      <FeatureGuide
        title="Sales Funnel Guide"
        description="Sales funnels guide customers through a strategic sequence: capture their email with a freebie (opt-in), offer a low-ticket product (tripwire), present your main offer, then upsell additional products. Each step is designed to build trust and increase value."
        steps={[
          "Click 'New Funnel' to create your first funnel",
          "Give your funnel a name (e.g., 'Freebie to Course Funnel')",
          "Create a URL slug (e.g., 'freebie-course') - this becomes your funnel URL",
          "Add an Opt-In step: Capture emails with a free resource",
          "Add a Tripwire step: Offer a low-ticket product (£5-27)",
          "Add a Main Offer step: Present your primary product",
          "Add an Upsell step: Offer additional value after purchase",
          "Configure each step with products, headlines, and descriptions",
          "Set status to 'Active' to make it live",
          "Copy the funnel URL and share it"
        ]}
        tips={[
          "Start with a valuable freebie to build your email list",
          "Tripwire offers should be irresistible at a low price",
          "Main offer should solve a major problem for your audience",
          "Upsells should complement the main offer",
          "Test different headlines and descriptions to improve conversions",
          "Use analytics to see which steps convert best",
          "A typical funnel: Opt-In → Tripwire → Main Offer → Upsell"
        ]}
      />

      <InfoBanner type="warning" title="Funnel Step Types Explained">
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li><strong>Opt-In:</strong> Free resource in exchange for email. Builds your list and qualifies leads. No payment required.</li>
          <li><strong>Tripwire:</strong> Low-ticket offer (£5-27) that's irresistible. Converts opt-ins into paying customers. Creates buying momentum.</li>
          <li><strong>Main Offer:</strong> Your primary product or service. This is what you really want to sell. Higher price point.</li>
          <li><strong>Upsell:</strong> Additional product offered after main purchase. One-click accept/decline. Increases average order value.</li>
          <li><strong>Order Bump:</strong> Add-on product at checkout. Checkbox to add to order. Low friction upsell.</li>
        </ul>
      </InfoBanner>

      {funnels.length === 0 ? (
        <EmptyState
          icon="default"
          title="No funnels yet"
          description="Create your first sales funnel to guide customers through your offers. Build opt-in pages, tripwires, main offers, and upsells."
          actionLabel="Create Funnel"
          onAction={() => {
            setEditingFunnel(null);
            setShowModal(true);
          }}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {funnels.map(funnel => {
            const funnelAnalytics = analytics[funnel.id] || {};
            return (
              <div key={funnel.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 mb-1">{funnel.name || 'Untitled Funnel'}</h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          funnel.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {funnel.status || 'draft'}
                        </span>
                        {funnel.slug && (
                          <span className="text-xs text-gray-500">/{funnel.slug}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleStatus(funnel.id, funnel.status)}
                        className="p-2 text-gray-400 hover:text-[#c5a059] transition"
                        title={funnel.status === 'active' ? 'Pause' : 'Activate'}
                      >
                        {funnel.status === 'active' ? <Pause size={18} /> : <Play size={18} />}
                      </button>
                      <button
                        onClick={() => {
                          setEditingFunnel(funnel);
                          setShowModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-[#c5a059] transition"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(funnel.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Steps Preview */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      {funnel.steps?.length || 0} step{(funnel.steps?.length || 0) !== 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {funnel.steps?.slice(0, 5).map((step, idx) => {
                        const stepConfig = STEP_TYPES[step.type];
                        if (!stepConfig) return null;
                        return (
                          <div key={idx} className="flex items-center gap-1 text-xs">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs`}
                              style={{ backgroundColor: stepConfig.color === 'blue' ? '#3b82f6' : 
                                            stepConfig.color === 'yellow' ? '#eab308' :
                                            stepConfig.color === 'green' ? '#10b981' :
                                            stepConfig.color === 'purple' ? '#8b5cf6' : '#f97316' }}>
                              {idx + 1}
                            </div>
                            {idx < (funnel.steps?.length || 0) - 1 && (
                              <ArrowRight size={12} className="text-gray-400" />
                            )}
                          </div>
                        );
                      })}
                      {(funnel.steps?.length || 0) > 5 && (
                        <span className="text-xs text-gray-400">+{(funnel.steps?.length || 0) - 5} more</span>
                      )}
                    </div>
                  </div>

                  {/* Analytics Preview */}
                  {funnelAnalytics.views > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Views</p>
                        <p className="text-sm font-semibold text-gray-800">{funnelAnalytics.views}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Opt-Ins</p>
                        <p className="text-sm font-semibold text-gray-800">{funnelAnalytics.optIns}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Sales</p>
                        <p className="text-sm font-semibold text-gray-800">{funnelAnalytics.purchases}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {funnel.slug && (
                      <>
                        <a
                          href={`/funnel/${funnel.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                        >
                          <Eye size={16} />
                          Preview
                        </a>
                        <button
                          onClick={() => copyFunnelUrl(funnel.slug)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                        >
                          <Copy size={16} />
                          Copy URL
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setEditingFunnel(funnel);
                        setShowModal(true);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        setAnalyticsFunnel(funnel);
                        setShowAnalyticsModal(true);
                        // Load detailed analytics
                        try {
                          const detailed = await getFunnelAnalytics(funnel.id);
                          setDetailedAnalytics(detailed);
                        } catch (error) {
                          console.error('Error loading detailed analytics:', error);
                          showToast('Failed to load analytics', 'error');
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
                    >
                      <BarChart3 size={16} />
                      Analytics
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <FunnelEditorModal
          funnel={editingFunnel}
          products={products}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingFunnel(null);
          }}
        />
      )}

      {showAnalyticsModal && analyticsFunnel && (
        <FunnelAnalyticsModal
          funnel={analyticsFunnel}
          analytics={detailedAnalytics || analytics[analyticsFunnel.id] || {}}
          onClose={() => {
            setShowAnalyticsModal(false);
            setAnalyticsFunnel(null);
            setDetailedAnalytics(null);
          }}
        />
      )}
    </div>
  );
}

// Funnel Editor Modal
function FunnelEditorModal({ funnel, products, onSave, onClose }) {
  const [name, setName] = useState(funnel?.name || '');
  const [slug, setSlug] = useState(funnel?.slug || '');
  const [status, setStatus] = useState(funnel?.status || 'draft');
  const [steps, setSteps] = useState(funnel?.steps || []);
  const [editingStep, setEditingStep] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);

  const handleAddStep = (type) => {
    const newStep = {
      id: `step_${Date.now()}`,
      type,
      order: steps.length,
      title: '',
      productId: ''
    };
    setSteps([...steps, newStep]);
    setEditingStep(newStep);
    setShowStepModal(true);
  };

  const handleEditStep = (step) => {
    setEditingStep(step);
    setShowStepModal(true);
  };

  const handleSaveStep = (updatedStep) => {
    setSteps(steps.map(s => s.id === updatedStep.id ? updatedStep : s));
    setShowStepModal(false);
    setEditingStep(null);
  };

  const handleDeleteStep = (stepId) => {
    if (!confirm('Delete this step?')) return;
    setSteps(steps.filter(s => s.id !== stepId).map((s, idx) => ({ ...s, order: idx })));
  };

  const handleMoveStep = (stepId, direction) => {
    const index = steps.findIndex(s => s.id === stepId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    const newSteps = [...steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setSteps(newSteps.map((s, idx) => ({ ...s, order: idx })));
  };

  const handleSave = () => {
    if (!name) {
      alert('Please enter a funnel name');
      return;
    }
    onSave({
      name,
      slug: slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      status,
      steps: steps.map((s, idx) => ({ ...s, order: idx }))
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-800">
            {funnel ? 'Edit Funnel' : 'New Funnel'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Funnel Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                placeholder="e.g. Freebie to Course Funnel"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                placeholder="funnel-slug"
              />
              <p className="text-xs text-gray-500 mt-1">
                URL: {window.location.origin}/funnel/{slug || 'slug'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">Funnel Steps</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAddStep('optIn')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition"
                >
                  <Mail size={16} />
                  Opt-In
                </button>
                <button
                  onClick={() => handleAddStep('tripwire')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-yellow-50 hover:bg-yellow-100 text-yellow-700 rounded-lg transition"
                >
                  <Gift size={16} />
                  Tripwire
                </button>
                <button
                  onClick={() => handleAddStep('mainOffer')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition"
                >
                  <ShoppingCart size={16} />
                  Main Offer
                </button>
                <button
                  onClick={() => handleAddStep('upsell')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition"
                >
                  <TrendingUp size={16} />
                  Upsell
                </button>
              </div>
            </div>

            {steps.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <p className="text-gray-500 mb-4">No steps yet. Add your first step to start building the funnel.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {steps.sort((a, b) => a.order - b.order).map((step, index) => (
                  <div key={step.id} className="relative">
                    <FunnelStep
                      step={step}
                      stepNumber={index + 1}
                      onEdit={handleEditStep}
                      onDelete={handleDeleteStep}
                      onMove={handleMoveStep}
                      products={products}
                    />
                    {index < steps.length - 1 && (
                      <div className="flex justify-center my-2">
                        <ArrowRight className="text-gray-400" size={20} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
            >
              Save Funnel
            </button>
          </div>
        </div>
      </div>

      {showStepModal && editingStep && (
        <StepEditorModal
          step={editingStep}
          products={products}
          onSave={handleSaveStep}
          onClose={() => {
            setShowStepModal(false);
            setEditingStep(null);
          }}
        />
      )}
    </div>
  );
}

// Step Editor Modal
function StepEditorModal({ step, products, onSave, onClose }) {
  const [title, setTitle] = useState(step?.title || '');
  const [productId, setProductId] = useState(step?.productId || '');
  const [headline, setHeadline] = useState(step?.headline || '');
  const [description, setDescription] = useState(step?.description || '');
  const [orderBump, setOrderBump] = useState(step?.orderBump || null);

  const selectedProduct = products.find(p => p.id === productId);

  const handleSave = () => {
    if (step.type !== 'optIn' && !productId) {
      alert('Please select a product');
      return;
    }
    // Only store productId, not productTitle - look it up dynamically when displaying
    const { productTitle, ...stepWithoutTitle } = step;
    onSave({
      ...stepWithoutTitle,
      title,
      productId,
      headline,
      description,
      orderBump
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-800">Edit Step</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Step Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              placeholder="e.g. Free Guide Opt-In"
            />
          </div>

          {step.type !== 'optIn' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              >
                <option value="">Select a product...</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.title} - £{product.price}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Headline</label>
            <input
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              placeholder="Compelling headline for this step"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
              placeholder="Describe the offer..."
            />
          </div>

          {step.type === 'mainOffer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Bump (Optional)</label>
              <div className="space-y-3">
                <input
                  type="text"
                  value={orderBump?.title || ''}
                  onChange={(e) => setOrderBump({ ...orderBump, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                  placeholder="Order bump title"
                />
                <select
                  value={orderBump?.productId || ''}
                  onChange={(e) => setOrderBump({ ...orderBump, productId: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                >
                  <option value="">Select product for order bump...</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.title} - £{product.price}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
            >
              Save Step
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

